import React, { useRef, useState, useEffect } from 'react';
import { Card, Button, Form, Dimmer } from "tabler-react";
import Select from 'react-select';
import { useFetch } from '../../../lib/useFetch';
import PutObject from '../../../lib/putObject';
import { toBase64 } from '../../../lib/helpers';

const StoreEditModal = ({ recordId, closeMainModal, reload }) => {
  const { data, loading, handleChange } = useFetch(`stores/${recordId}`);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { data: categories, loading: categoriesLoading } = useFetch(`categories`);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasNewPhoto, setHasNewPhoto] = useState(false);
  const [hasNewLogo, setHasNewLogo] = useState(false);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [removeLogo, setRemoveLogo] = useState(false);
  const photoFile = useRef(null);
  const logoFile = useRef(null);

  useEffect(() => {
    if (!loading && data) {
      if (data.categoryId) {
        setSelectedCategory({
          label: data.categoryId.name,
          value: data.categoryId._id
        });
      } else {
        setSelectedCategory(null);
      }
    }
  }, [loading, data]);

  const validateForm = () => {
    if (!data?.name) {
      setError('Please enter store name');
      return false;
    }
    if (!data?.url) {
      setError('Please enter store URL');
      return false;
    }
    if (photoFile.current?.files.length > 0 && !photoFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file for photo');
      return false;
    }
    if (logoFile.current?.files.length > 0 && !logoFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file for logo');
      return false;
    }
    return true;
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setPhotoPreview(preview);
      setHasNewPhoto(true);
      setRemovePhoto(false);
      setError('');
    } else if (file) {
      setError('Please select a valid image file for photo');
      setPhotoPreview(null);
      setHasNewPhoto(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setLogoPreview(preview);
      setHasNewLogo(true);
      setRemoveLogo(false);
      setError('');
    } else if (file) {
      setError('Please select a valid image file for logo');
      setLogoPreview(null);
      setHasNewLogo(false);
    }
  };

  const handlePhotoDropZoneClick = () => {
    photoFile.current.click();
  };

  const handleLogoDropZoneClick = () => {
    logoFile.current.click();
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    setPhotoPreview(null);
    setHasNewPhoto(false);
    setRemovePhoto(true);
    photoFile.current.value = '';
    // Force reset the file input
    if (photoFile.current) {
      photoFile.current.type = '';
      photoFile.current.type = 'file';
    }
  };

  const handleRemoveLogo = (e) => {
    e.stopPropagation();
    setLogoPreview(null);
    setHasNewLogo(false);
    setRemoveLogo(true);
    logoFile.current.value = '';
    // Force reset the file input
    if (logoFile.current) {
      logoFile.current.type = '';
      logoFile.current.type = 'file';
    }
  };

  const saveEdit = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);
      
      const editedStore = {
        _id: recordId,
        name: data.name,
        url: data.url,
        categoryId: selectedCategory?.value || null
      };

      // Handle photo
      if (removePhoto) {
        editedStore.image = '';
      } else if (hasNewPhoto && photoFile.current?.files.length > 0) {
        editedStore.image = await toBase64(photoFile.current.files[0]);
      }

      // Handle logo
      if (removeLogo) {
        editedStore.logo = '';
      } else if (hasNewLogo && logoFile.current?.files.length > 0) {
        editedStore.logo = await toBase64(logoFile.current.files[0]);
      }
      
      await PutObject('stores', editedStore);
      
      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to update store. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Get current photo source for preview
  const getCurrentPhotoSrc = () => {
    if (removePhoto) return null;
    if (hasNewPhoto && photoPreview) {
      return photoPreview;
    }
    return data?.image;
  };

  // Get current logo source for preview
  const getCurrentLogoSrc = () => {
    if (removeLogo) return null;
    if (hasNewLogo && logoPreview) {
      return logoPreview;
    }
    return data?.logo;
  };

  return (
    <Card>
      <Dimmer active={loading} loader={loading}>
        <Card.Header>
          <Card.Title>Edit Store</Card.Title>
          <Card.Options>
            <Button size="sm" icon="x" onClick={closeMainModal} />
          </Card.Options>
        </Card.Header>

        {!loading && data && (
          <>
            <Card.Body>
              {error && <div className="alert alert-danger">{error}</div>}
              <Form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                <Form.FieldSet>
                  <Form.Input
                    name='name'
                    label='Name'
                    placeholder='Enter store name'
                    value={data.name || ''}
                    onChange={handleChange}
                    required
                  />

                  <Form.Input
                    name='url'
                    label='URL'
                    placeholder='Enter store URL'
                    value={data.url || ''}
                    onChange={handleChange}
                    required
                  />

                  <Form.Label>Category</Form.Label>
                  <Select
                    value={selectedCategory}
                    options={
                      categories?.map(cat => ({
                        label: cat.name,
                        value: cat._id
                      })) || []
                    }
                    isLoading={categoriesLoading}
                    onChange={setSelectedCategory}
                    isClearable
                    placeholder="Select a category"
                  />

                  <Form.Group>
                    <Form.Label>Store Photo</Form.Label>
                    <input 
                      type='file' 
                      ref={photoFile}
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                    
                    {!getCurrentPhotoSrc() ? (
                      <div className="drop-zone" onClick={handlePhotoDropZoneClick}>
                        <div className="drop-zone-icon">
                          <svg 
                            width="48" 
                            height="48" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="#6c757d" 
                            strokeWidth="1.5" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21,15 16,10 5,21"/>
                          </svg>
                        </div>
                        <div className="drop-zone-text">
                          {removePhoto ? 'Click here to upload new photo' : 'Click here to upload store photo'}
                        </div>
                        <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                      </div>
                    ) : (
                      <div className="image-preview-container">
                        <img 
                          src={getCurrentPhotoSrc()} 
                          alt="Store Photo Preview" 
                          className="image-preview"
                          onClick={handlePhotoDropZoneClick}
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={handleRemovePhoto}
                        >
                          ×
                        </button>
                        {!hasNewPhoto && data?.image && !removePhoto && (
                          <div className="image-note">Current photo shown. Click to change.</div>
                        )}
                        {hasNewPhoto && (
                          <div className="image-note">New photo selected. Click to change.</div>
                        )}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label>Store Logo</Form.Label>
                    <input 
                      type='file' 
                      ref={logoFile}
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ display: 'none' }}
                    />
                    
                    {!getCurrentLogoSrc() ? (
                      <div className="drop-zone" onClick={handleLogoDropZoneClick}>
                        <div className="drop-zone-icon">
                          <svg 
                            width="48" 
                            height="48" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="#6c757d" 
                            strokeWidth="1.5" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21,15 16,10 5,21"/>
                          </svg>
                        </div>
                        <div className="drop-zone-text">
                          {removeLogo ? 'Click here to upload new logo' : 'Click here to upload store logo'}
                        </div>
                        <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                      </div>
                    ) : (
                      <div className="image-preview-container">
                        <img 
                          src={getCurrentLogoSrc()} 
                          alt="Store Logo Preview" 
                          className="image-preview"
                          onClick={handleLogoDropZoneClick}
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={handleRemoveLogo}
                        >
                          ×
                        </button>
                        {!hasNewLogo && data?.logo && !removeLogo && (
                          <div className="image-note">Current logo shown. Click to change.</div>
                        )}
                        {hasNewLogo && (
                          <div className="image-note">New logo selected. Click to change.</div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                </Form.FieldSet>
              </Form>
            </Card.Body>
            <Card.Footer>
              <div className="modal-footer">
                <Button
                  color="primary"
                  size="sm"
                  className={`ms-2 ${saveLoading ? 'btn-loading' : ''}`}
                  onClick={saveEdit}
                  disabled={saveLoading}
                >
                  Save Changes
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  className="ms-2"
                  onClick={closeMainModal}
                  disabled={saveLoading}
                >
                  Cancel
                </Button>
              </div>
            </Card.Footer>
          </>
        )}
      </Dimmer>
    </Card>
  );
};

export default StoreEditModal;